import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { EnumItem, Meta, Project } from './types';
import { setLanguage, t, useLanguage } from './i18n';

interface MetaCtx {
  meta: Meta;
  /** selected project, 0 = all projects */
  projectId: number;
  setProjectId: (id: number) => void;
  label: (enumName: string, value: number | null | undefined) => string;
  items: (enumName: string) => EnumItem[];
  statusColor: (value: number) => string;
  project: (id: number) => Project | undefined;
  /** effective access level in a project (or the global level for "all projects") */
  level: (projectId?: number) => number;
  th: (name: string) => number;
}

const Ctx = createContext<MetaCtx | null>(null);
const STORAGE_KEY = 'bt.project';

function readStoredProject(): number | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v == null ? null : Number(v);
  } catch {
    return null;
  }
}

export function MetaProvider({ children }: { children: ReactNode }) {
  const { data, error, isLoading } = useQuery({ queryKey: ['meta'], queryFn: () => api.get<Meta>('/api/meta'), staleTime: 60_000 });
  const lang = useLanguage();
  // the language saved in the account wins over the browser's last choice
  useEffect(() => {
    if (data?.prefs?.language) setLanguage(data.prefs.language);
  }, [data?.prefs?.language]);
  useEffect(() => {
    const root = document.documentElement;
    if (data?.prefs?.dark_mode === true) root.dataset.theme = 'dark';
    else delete root.dataset.theme;
    return () => {
      delete root.dataset.theme;
    };
  }, [data?.prefs?.dark_mode]);
  const [projectId, setProjectState] = useState<number | null>(() => readStoredProject());

  const setProjectId = useCallback((id: number) => {
    setProjectState(id);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      /* storage unavailable */
    }
  }, []);

  const value = useMemo<MetaCtx | null>(() => {
    if (!data) return null;
    const byId = new Map(data.projects.map((p) => [p.id, p]));
    let pid = projectId ?? data.prefs?.default_project_id ?? 0;
    if (pid && !byId.has(pid)) pid = 0;
    return {
      meta: data,
      projectId: pid,
      setProjectId,
      label: (name, v) => {
        const l = data.enums[name]?.find((e) => e.value === v)?.label;
        return l != null ? t(l) : v == null ? '' : `@${v}@`;
      },
      items: (name) => (data.enums[name] ?? []).map((e) => ({ ...e, label: t(e.label) })),
      statusColor: (v) => data.enums.status.find((e) => e.value === v)?.color ?? '#ddd',
      project: (id) => byId.get(id),
      level: (id) => (id ? (byId.get(id)?.access ?? 0) : data.user.access_level),
      th: (name) => Number(data.thresholds[name]),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, projectId, setProjectId, lang]);

  if (isLoading) return <div className="center muted">{t('Loading…')}</div>;
  if (error || !value) return <div className="center error">{t('Could not load: {error}', { error: (error as Error)?.message })}</div>;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMeta(): MetaCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMeta outside MetaProvider');
  return v;
}
